import { downloadData } from "aws-amplify/storage"
import { getConnectionsLookupPath } from "@/config/backend"
import { env } from "@/env.setup"
import { TODO } from "@/types/utility"

export type Connection = {
  to_id: string
  route_long_name: string
  agency_name: string
  self_intersect: boolean
}

export type ReverseConnection = {
  from_id: string
  route_long_name: string
  agency_name: string
  self_intersect: boolean
}

export type Lookup = Record<string, Connection[]>
export type ReverseLookup = Record<string, ReverseConnection[]>

function createReverseLookup(lookup: Lookup): ReverseLookup {
  const reverseLookup: ReverseLookup = {}

  for (const from_id in lookup) {
    if (Object.hasOwnProperty.call(lookup, from_id)) {
      const connections = lookup[from_id]

      connections?.forEach((connection) => {
        const to_id = connection.to_id

        const reverseEntry = {
          from_id: from_id,
          route_long_name: connection.route_long_name,
          agency_name: connection.agency_name,
          self_intersect: connection.self_intersect,
        }

        if (!reverseLookup[to_id]) {
          reverseLookup[to_id] = []
        }
        reverseLookup[to_id].push(reverseEntry)
      })
    }
  }
  return reverseLookup
}

/**
 * Asynchronously fetches and parses the connections lookup from S3.
 * Falls back to local static file if request fails.
 */
export async function getLookup(version?: string): Promise<{
  lookup: Lookup,
  reverseLookup: ReverseLookup,
  idToRouteLongNameMap: Record<string, string>
}> {
  let lookup: Lookup
  try {
    if (!version) throw new Error('No version provided')
    const path = getConnectionsLookupPath(version)
    const { body } = await downloadData({ path, options: { bucket: { bucketName: env.VITE_AWS_S3_DOWNLOAD_BUCKET_NAME, region: "us-east-2" }, } }).result
    const raw = await body.text()
    lookup = JSON.parse(raw)
  } catch (e) {
    console.error(`[getLookup]: UNABLE TO FIND / PARSE JSON FILE`, e)
    lookup = await getDefaultLookup()
  }

  return createLookupAndReverseLookup(lookup)
}

async function getDefaultLookup(): Promise<TODO> {
    const localMod = await import("@/assets/connections_lookup.json")
    return localMod.default || localMod
}


function createLookupAndReverseLookup(lookup: Lookup) {
  const reverseLookup = createReverseLookup(lookup)

  const idToRouteLongNameMap: Record<string, string> = Object.values(lookup)
    .flat()
    .reduce<Record<string, string>>((acc, conn) => {
      if (
        conn &&
        typeof conn.to_id === "string" &&
        typeof conn.route_long_name === "string"
      ) {
        acc[conn.to_id] = conn.route_long_name
      }
      return acc
    }, {})

  return { lookup, reverseLookup, idToRouteLongNameMap }
}

export async function getFromServerFallback(): Promise<TODO> {
  const lookup = await getDefaultLookup();
  return createLookupAndReverseLookup(lookup)
}